import { useState, useEffect, useRef, useCallback } from "react"
import { Menu, X, ChevronDown, Rocket, Bot, Users } from "lucide-react"

const services = [
  { href: "/services/mvp-development", label: "MVP Development", description: "Ship your product in 30 days", icon: Rocket },
  { href: "/services/ai-workflow-automation", label: "AI Workflow Automation", description: "10x your team with AI", icon: Bot },
  { href: "/services/dedicated-developers", label: "Dedicated Developers", description: "Scale without hiring", icon: Users },
]

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/careers", label: "Careers" },
  { href: "/#contact", label: "Contact" },
]

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [isServicesOpen, setIsServicesOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const servicesRef = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const openServices = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    setIsServicesOpen(true)
  }, [])

  const closeServices = useCallback(() => {
    closeTimerRef.current = setTimeout(() => setIsServicesOpen(false), 100)
  }, [])

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target as Node)) {
        setIsServicesOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    setIsOpen(false)
    setIsServicesOpen(false)

    if (href.includes("#")) {
      const [, hash] = href.split("#")
      const isHome = window.location.pathname === "/" || window.location.pathname === ""
      if (isHome) {
        e.preventDefault()
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" })
      }
    } else {
      window.scrollTo({ top: 0, behavior: "instant" })
    }
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
        isScrolled
          ? "bg-white/80 backdrop-blur-xl shadow-[0_1px_3px_rgba(0,0,0,0.05),0_8px_24px_rgba(0,0,0,0.05)] border-b border-slate-200/50"
          : "bg-transparent"
      }`}
    >
      <div className="container flex h-16 items-center justify-between px-6">
        <a href="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="Optimal Dev Labs" className="w-[50px] h-auto" />
          <span className="font-bold text-lg hidden sm:inline-block">Optimal Dev Labs</span>
        </a>

        <nav className="hidden md:flex items-center gap-6">
          <div ref={servicesRef} className="relative" onMouseEnter={openServices} onMouseLeave={closeServices}>
            <button
              type="button"
              onClick={() => setIsServicesOpen(!isServicesOpen)}
              className="flex items-center gap-1 text-sm font-medium hover:text-[#ed2132] transition-colors cursor-pointer"
            >
              Services
              <ChevronDown className={`h-4 w-4 transition-transform ${isServicesOpen ? "rotate-180" : ""}`} />
            </button>

            {isServicesOpen && (
              <div className="absolute top-full left-0 mt-2 w-72 bg-white/95 backdrop-blur-xl rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.08)] border border-slate-200/60 py-2" onMouseEnter={openServices} onMouseLeave={closeServices}>
                {services.map((service) => {
                  const Icon = service.icon
                  return (
                    <a
                      key={service.href}
                      href={service.href}
                      onClick={(e) => handleNavClick(e, service.href)}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50/80 transition-all duration-200 group"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        <Icon className="h-5 w-5 text-[#ed2132]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900 group-hover:text-[#ed2132] transition-colors">{service.label}</p>
                        <p className="text-xs text-slate-500">{service.description}</p>
                      </div>
                    </a>
                  )
                })}
              </div>
            )}
          </div>

          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-sm font-medium hover:text-[#ed2132] transition-colors"
            >
              {link.label}
            </a>
          ))}

          <a
            href="/#contact"
            onClick={(e) => handleNavClick(e, "/#contact")}
            className="inline-flex items-center justify-center rounded-lg font-medium transition-colors text-white hover:opacity-90 px-4 py-2 text-sm"
            style={{ backgroundColor: "#ed2132" }}
          >
            Book a Call
          </a>
        </nav>

        <button
          type="button"
          className="md:hidden p-2 cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close menu" : "Open menu"}
        >
          {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {isOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-slate-200/60">
          <nav className="container flex flex-col px-6 py-4 gap-2">
            <div className="py-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Services</p>
              {services.map((service) => {
                const Icon = service.icon
                return (
                  <a
                    key={service.href}
                    href={service.href}
                    onClick={(e) => handleNavClick(e, service.href)}
                    className="flex items-center gap-3 py-2 text-sm font-medium hover:text-[#ed2132] transition-colors"
                  >
                    <Icon className="h-4 w-4 text-[#ed2132]" />
                    {service.label}
                  </a>
                )
              })}
            </div>

            <div className="border-t pt-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="block text-sm font-medium hover:text-[#ed2132] transition-colors py-2"
                >
                  {link.label}
                </a>
              ))}
            </div>

            <a
              href="/#contact"
              onClick={(e) => handleNavClick(e, "/#contact")}
              className="inline-flex items-center justify-center rounded-lg font-medium transition-colors text-white hover:opacity-90 px-4 py-2 text-sm w-full text-center mt-2"
              style={{ backgroundColor: "#ed2132" }}
            >
              Book a Call
            </a>
          </nav>
        </div>
      )}
    </header>
  )
}
